import React, { useEffect, useState } from 'react';
import GaugeChart from 'react-gauge-chart';

import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';

import { format } from 'utils/format';
import { TermInfo } from 'components/term-info/term-info.component';
import {
  selfConsumptionKwh,
  totalConsumptionKwh,
} from 'utils/invoiceConsumption';

import {
  ChartContainer,
  MeanConsumptionContainer,
} from './mean-consumption.styles';

interface MeanConsumptionProps {
  consumerUnitList: ConsumerUnit[];
  invoiceList: Invoice[];
  invoiceRefMonthList: string[];
}

export const MeanConsumption = ({
  consumerUnitList,
  invoiceList,
  invoiceRefMonthList,
}: MeanConsumptionProps) => {
  const [needle, setNeedle] = useState<number>(0);
  // Período ÷ média dos 12 meses (1 = na média); null sem média
  const [ratioVsMean, setRatioVsMean] = useState<number | null>(null);
  const [meanConsumptionLastTwelveMonths, setMeanConsumtionLastTwelveMonths] =
    useState<number>(0);

  // Média mensal do consumo TOTAL das UCs selecionadas (sem seleção = todas):
  // em cada mês soma as UCs (distribuidora + autoconsumo solar) e depois tira a
  // média entre os meses com dado. Até 30/set/2026 era a média POR UC — o card
  // mostrava ~1/3 do consumo de 3 UCs. Mês em que alguma UC não tem dado soma
  // só as que têm.
  useEffect(() => {
    const monthlyMean = (totals: (number | null)[]) => {
      const valid = totals.filter((x): x is number => x !== null);
      return valid.length
        ? valid.reduce((acc, item) => acc + item, 0) / valid.length
        : 0;
    };

    // Últimos 12 meses: série mensal da UC (histórico impresso + faturas)
    const lastTwelveTotals = getLastTwelveRefMonth().map((refMonth) => {
      let total: number | null = null;
      for (const consumerUnit of consumerUnitList) {
        const consumption = consumerUnit.monthConsumptionList.find(
          (y) => y.refMonth === refMonth
        )?.value;
        if (!consumption) continue;
        total =
          (total ?? 0) +
          consumption +
          selfConsumptionKwh(consumerUnit, refMonth);
      }
      return total;
    });
    const meanLastTwelveMonths = monthlyMean(lastTwelveTotals);
    setMeanConsumtionLastTwelveMonths(meanLastTwelveMonths);

    // Período do filtro: faturas das UCs selecionadas
    const periodTotals = invoiceRefMonthList.map((refMonth) => {
      let total: number | null = null;
      for (const consumerUnit of consumerUnitList) {
        const invoice = invoiceList.find(
          (x) =>
            x.consumerUnit.id === consumerUnit.id && x.refMonth === refMonth
        );
        if (!invoice) continue;
        total = (total ?? 0) + totalConsumptionKwh(invoice, consumerUnit);
      }
      return total;
    });
    const meanConsumptionInPeriod = monthlyMean(periodTotals);

    // Ponteiro = consumo do PERÍODO ÷ média dos 12 meses: 1,0 = na média
    // (limite verde/amarelo), 1,1 / 1,2 / 1,3 = +10% / +20% / +30%. Até
    // 30/set/2026 a razão estava invertida (média ÷ período).
    const diff = !meanLastTwelveMonths
      ? 0
      : meanConsumptionInPeriod / meanLastTwelveMonths;

    const needle = diff > 1 ? ((diff - 1) * 0.75) / 0.3 : diff * 0.25;

    setNeedle(Math.min(1, Math.max(0, needle)));
    // Escala do ponteiro em texto (out/2026): o arco não dizia o que é "verde"
    setRatioVsMean(meanLastTwelveMonths ? diff : null);
  }, [consumerUnitList, invoiceRefMonthList, invoiceList]);

  const getLastTwelveRefMonth = () => {
    const date = new Date();
    const month = date.getMonth() + 1;
    let year = date.getFullYear();

    const lastTwelveRefMonth: string[] = [];

    for (let i = month; i >= 0; i--) {
      if (lastTwelveRefMonth.length === 12) {
        break;
      }
      lastTwelveRefMonth.push(`${year}_${i.toString().padStart(2, '0')}`);
      if (i === 1) {
        i = 13;
        year--;
      }
    }

    return lastTwelveRefMonth.reverse();
  };

  // Explicação do ponteiro no "i" do título (30/out/2026, pedido do Bruno —
  // antes ficava em texto corrido abaixo do valor).
  const SCALE_NOTE =
    'Verde = até a média; amarelo, laranja e vermelho = +10%, +20% e +30%.';
  const meanNote =
    ratioVsMean === null
      ? SCALE_NOTE
      : `${
          Math.abs(ratioVsMean - 1) < 0.005
            ? 'O período selecionado está na média.'
            : `O período selecionado está ${format.percentageNoDecimal(
                Math.abs(ratioVsMean - 1)
              )}% ${ratioVsMean > 1 ? 'acima' : 'abaixo'} da média.`
        } ${SCALE_NOTE}`;

  return (
    <MeanConsumptionContainer className="p-4">
      <h4 className="mb-4">
        Média de consumo
        <TermInfo text={meanNote} />
      </h4>
      <ChartContainer className="d-flex justify-content-center">
        <GaugeChart
          style={{ width: '300px', height: '20%' }}
          hideText
          nrOfLevels={4}
          cornerRadius={1}
          arcWidth={0.6}
          percent={needle || 0}
          arcPadding={0.04}
          animateDuration={1000}
          animate={false}
          colors={['#37b34a', '#f9d815', '#F79422', '#F42121']}
          needleColor="#152e63"
          needleBaseColor="#152e63"
        />
      </ChartContainer>
      <h2 className="mb-2">
        {format.energy(meanConsumptionLastTwelveMonths)} kWh
      </h2>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span>Média mensal das UCs selecionadas (últimos 12 meses)</span>
      </div>
    </MeanConsumptionContainer>
  );
};
